import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { Plus, Pencil, Trash2, ArrowUp, ArrowDown } from "lucide-react";

interface FAQ {
  id: string;
  category: string;
  question: string;
  answer: string;
  question_en: string | null;
  answer_en: string | null;
  question_fr: string | null;
  answer_fr: string | null;
  sort_order: number;
  is_active: boolean;
}

const AdminFAQ = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingFaq, setEditingFaq] = useState<FAQ | null>(null);
  const [selectedLang, setSelectedLang] = useState<"es" | "en" | "fr">("es");
  const [formData, setFormData] = useState({
    category: "global",
    question: "",
    answer: "",
    question_en: "",
    answer_en: "",
    question_fr: "",
    answer_fr: "",
  });
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: faqs, isLoading } = useQuery({
    queryKey: ["admin-faqs"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("faqs")
        .select("*")
        .order("category")
        .order("sort_order", { ascending: true });
      if (error) throw error;
      return data as FAQ[];
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (data: typeof formData & { id?: string }) => {
      if (data.id) {
        const { error } = await supabase
          .from("faqs")
          .update({
            category: data.category,
            question: data.question,
            answer: data.answer,
            question_en: data.question_en || null,
            answer_en: data.answer_en || null,
            question_fr: data.question_fr || null,
            answer_fr: data.answer_fr || null,
          })
          .eq("id", data.id);
        if (error) throw error;
      } else {
        const maxOrder = faqs?.filter(f => f.category === data.category)
          .reduce((max, f) => Math.max(max, f.sort_order), -1) ?? -1;
        const { error } = await supabase.from("faqs").insert({
          category: data.category,
          question: data.question,
          answer: data.answer,
          question_en: data.question_en || null,
          answer_en: data.answer_en || null,
          question_fr: data.question_fr || null,
          answer_fr: data.answer_fr || null,
          sort_order: maxOrder + 1,
        });
        if (error) throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-faqs"] });
      setIsDialogOpen(false);
      resetForm();
      toast({ title: editingFaq ? "FAQ actualizada" : "FAQ creada" });
    },
    onError: () => {
      toast({ title: "Error al guardar", variant: "destructive" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("faqs").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-faqs"] });
      toast({ title: "FAQ eliminada" });
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      const { error } = await supabase
        .from("faqs")
        .update({ is_active })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-faqs"] });
    },
  });

  const reorderMutation = useMutation({
    mutationFn: async ({ id, direction }: { id: string; direction: "up" | "down" }) => {
      const faq = faqs?.find((f) => f.id === id);
      if (!faq) return;
      
      const categoryFaqs = faqs?.filter((f) => f.category === faq.category)
        .sort((a, b) => a.sort_order - b.sort_order) || [];
      const currentIndex = categoryFaqs.findIndex((f) => f.id === id);
      const swapIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
      
      if (swapIndex < 0 || swapIndex >= categoryFaqs.length) return;
      
      const swapFaq = categoryFaqs[swapIndex];
      
      await supabase.from("faqs").update({ sort_order: swapFaq.sort_order }).eq("id", id);
      await supabase.from("faqs").update({ sort_order: faq.sort_order }).eq("id", swapFaq.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-faqs"] });
    },
  });

  const resetForm = () => {
    setFormData({
      category: "global",
      question: "",
      answer: "",
      question_en: "",
      answer_en: "",
      question_fr: "",
      answer_fr: "",
    });
    setEditingFaq(null);
    setSelectedLang("es");
  };

  const openCreate = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const openEdit = (faq: FAQ) => {
    setEditingFaq(faq);
    setFormData({
      category: faq.category,
      question: faq.question,
      answer: faq.answer,
      question_en: faq.question_en || "",
      answer_en: faq.answer_en || "",
      question_fr: faq.question_fr || "",
      answer_fr: faq.answer_fr || "",
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.question || !formData.answer) {
      toast({ title: "Pregunta y respuesta en español son obligatorias", variant: "destructive" });
      return;
    }
    saveMutation.mutate({ ...formData, id: editingFaq?.id });
  };

  const getCategoryLabel = (cat: string) => {
    const labels: Record<string, string> = {
      global: "Global",
      europa: "Europa",
      norte_america: "Norte América",
    };
    return labels[cat] || cat;
  };

  const categories = ["global", "europa", "norte_america"];

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">Gestionar FAQ</h1>
        <Button onClick={openCreate}>
          <Plus className="mr-2 h-4 w-4" /> Nueva Pregunta
        </Button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      ) : (
        <Tabs defaultValue="global">
          <TabsList>
            {categories.map((cat) => (
              <TabsTrigger key={cat} value={cat}>
                {getCategoryLabel(cat)} ({faqs?.filter((f) => f.category === cat).length || 0})
              </TabsTrigger>
            ))}
          </TabsList>
          {categories.map((cat) => (
            <TabsContent key={cat} value={cat}>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Pregunta (ES)</TableHead>
                    <TableHead className="w-24">Activa</TableHead>
                    <TableHead className="w-24">Orden</TableHead>
                    <TableHead className="w-32">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {faqs
                    ?.filter((f) => f.category === cat)
                    .sort((a, b) => a.sort_order - b.sort_order)
                    .map((faq, index, arr) => (
                      <TableRow key={faq.id}>
                        <TableCell className="font-medium">{faq.question}</TableCell>
                        <TableCell>
                          <Switch
                            checked={faq.is_active}
                            onCheckedChange={(checked) =>
                              toggleActiveMutation.mutate({ id: faq.id, is_active: checked })
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="icon"
                              disabled={index === 0}
                              onClick={() => reorderMutation.mutate({ id: faq.id, direction: "up" })}
                            >
                              <ArrowUp className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              disabled={index === arr.length - 1}
                              onClick={() => reorderMutation.mutate({ id: faq.id, direction: "down" })}
                            >
                              <ArrowDown className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button variant="ghost" size="icon" onClick={() => openEdit(faq)}>
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => deleteMutation.mutate(faq.id)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TabsContent>
          ))}
        </Tabs>
      )}

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingFaq ? "Editar Pregunta" : "Nueva Pregunta"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Categoría</Label>
              <Select
                value={formData.category}
                onValueChange={(v) => setFormData({ ...formData, category: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="global">Global</SelectItem>
                  <SelectItem value="europa">Europa</SelectItem>
                  <SelectItem value="norte_america">Norte América</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Tabs value={selectedLang} onValueChange={(v) => setSelectedLang(v as "es" | "en" | "fr")}>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="es">🇪🇸 Español</TabsTrigger>
                <TabsTrigger value="en">🇬🇧 English</TabsTrigger>
                <TabsTrigger value="fr">🇫🇷 Français</TabsTrigger>
              </TabsList>
              <TabsContent value="es" className="space-y-4">
                <div>
                  <Label>Pregunta *</Label>
                  <Input
                    value={formData.question}
                    onChange={(e) => setFormData({ ...formData, question: e.target.value })}
                    required
                  />
                </div>
                <div>
                  <Label>Respuesta *</Label>
                  <Textarea
                    value={formData.answer}
                    onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
                    rows={6}
                    required
                  />
                </div>
              </TabsContent>
              <TabsContent value="en" className="space-y-4">
                <div>
                  <Label>Question</Label>
                  <Input
                    value={formData.question_en}
                    onChange={(e) => setFormData({ ...formData, question_en: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Answer</Label>
                  <Textarea
                    value={formData.answer_en}
                    onChange={(e) => setFormData({ ...formData, answer_en: e.target.value })}
                    rows={6}
                  />
                </div>
              </TabsContent>
              <TabsContent value="fr" className="space-y-4">
                <div>
                  <Label>Question</Label>
                  <Input
                    value={formData.question_fr}
                    onChange={(e) => setFormData({ ...formData, question_fr: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Réponse</Label>
                  <Textarea
                    value={formData.answer_fr}
                    onChange={(e) => setFormData({ ...formData, answer_fr: e.target.value })}
                    rows={6}
                  />
                </div>
              </TabsContent>
            </Tabs>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={saveMutation.isPending}>
                {saveMutation.isPending ? "Guardando..." : "Guardar"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminFAQ;